import React from 'react'
import { createSortableElement, SortableItem } from 'react-dnd-sortable'
import { HomeScreenWidget } from '~/models'
import { memo } from '~/ui/component'
import { ClearButton, Dimple, Empty, HBox, Label, List, ListItem, ListProps, SVG, VBox } from '~/ui/components'
import { useResourceTranslation } from '~/ui/resources'
import { createUseStyles, layout } from '~/ui/styling'
import { useHomeScreenWidgets } from './HomeScreenWidgetContext'

export const HOME_SCREEN_WIDGET = 'app.groundcontrol.sortable.HOME_SCREEN_WIDGET'

const SortableHomeScreenWidget = createSortableElement(VBox, {
  axis:     'vertical',
  itemType: HOME_SCREEN_WIDGET,
})

const HomeScreenWidgetsList = memo('HomeScreenWidgetsList', () => {

  const {t} = useResourceTranslation('projects')
  const {widgets, editingItems, moveWidget} = useHomeScreenWidgets()

  //------
  // Sorting

  const sortEnabled    = editingItems
  const sortableListID = React.useMemo(() => Symbol(), [])

  const handleSortDrop = React.useCallback(async (item: SortableItem, dropIndex: number) => {
    if (editingItems == null) { return }
    moveWidget(item.index, dropIndex)
  }, [editingItems, moveWidget])

  const sortable = React.useMemo((): ListProps<any>['sortable'] | undefined => {
    if (!sortEnabled) { return undefined }

    return {
      listID:     sortableListID,
      accept:     [HOME_SCREEN_WIDGET],
      onSortDrop: handleSortDrop,
    }
  }, [handleSortDrop, sortEnabled, sortableListID])

  //------
  // Rendering

  function render() {
    if (widgets.length === 0) return renderEmpty()
    return(
      <List
        flex
        sortable={sortable}
        keyExtractor={keyExtractor}
        renderItem={sortEnabled ? renderSortableItem : renderItem}
        renderPlaceholder={renderSortPlaceholder}
        data={widgets}
      />
    )
  }

  const renderEmpty = React.useCallback(() => (
    <Empty flex {...t('empty')} />
  ), [t])

  const keyExtractor = React.useCallback(
    (widget, index) => widget.type + index,
    [],
  )

  const renderItem = React.useCallback((tab: HomeScreenWidget, index: number) => (
      <VBox>
        <HomeScreenWidgetListItem widget={tab} index={index} />
        {index < widgets.length - 1 && <Dimple horizontal />}
      </VBox>
  ), [widgets])

  const renderSortableItem = React.useCallback((item: HomeScreenWidget, index: number) => (
    <SortableHomeScreenWidget index={index} sourceList={sortableListID} payload={item}>
      {renderItem(item, index)}
    </SortableHomeScreenWidget>
  ), [renderItem, sortableListID])

  const renderSortPlaceholder = React.useCallback(
    () => <VBox style={{height: sortPlaceholderHeight}}/>,
    [],
  )

  return render()

})

export default HomeScreenWidgetsList

const sortPlaceholderHeight = layout.barHeight.m + 2 // 2px for the dimple

export const iconSize = layout.icon.m

const useStyles = createUseStyles({
  item: {
    height: layout.barHeight.m,
    flex: [1, 0, 'auto'],
    overflow:  'hidden',
    '&.editingItems': {
      cursor: 'grab',
    },
  },
})

export interface HomeScreenWidgetListItemProps {
  widget: HomeScreenWidget
  index:  number
}

const HomeScreenWidgetListItem = memo('HomeScreenWidgetListItem', (props: HomeScreenWidgetListItemProps) => {

  const {widget, index} = props
  const {editingItems, setConfiguringItemIndex, openForm, removeWidget} = useHomeScreenWidgets()

  const {t} = useResourceTranslation('projects')

  const configureWidget = React.useCallback(() => {
    setConfiguringItemIndex(index)
    openForm()
  }, [setConfiguringItemIndex, openForm, index])

  const mayConfigure = !editingItems && widget.type !== 'agenda'

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return(
      <ListItem
        classNames={[$.item, {editingItems}]}
        spacious
        accessory={renderAccessory()}
        caption={renderCaption()}
      />
    )
  }

  function renderAccessory() {
    return(
      <HBox gap={layout.padding.s}>
        {editingItems ? (
          renderEditAccessory()
        ) : mayConfigure ? (
          renderConfigureButton()
        ) : null}
      </HBox>
    )
  }

  function renderCaption() {
    return(
      <HBox gap={layout.padding.s}>
        {renderIcon()}
        <Label children={t(`home-screen.widgets.types.${widget.type}`)}/>
      </HBox>
    )
  }

  function renderIcon() {
    return (
      <SVG
        name={HomeScreenWidget.iconForType(widget.type)}
        size={iconSize}
      />
    )
  }

  function renderConfigureButton() {
    return (
      <ClearButton
        onTap={configureWidget}
        icon='cog'
        aria-label={t('buttons:edit')}
      />
    )
  }

  function renderEditAccessory() {
    return (
      <HBox gap={layout.padding.inline.l}>
        <SVG
          name='sort-handle'
          size={layout.icon.m}
        />
        <ClearButton
          icon='cross-circle'
          aria-label={t('buttons:remove')}
          onTap={() => removeWidget(index)}
        />
      </HBox>
    )
  }

  return render()

})
